import {
  useState,
  useEffect,
  ChangeEvent,
  FocusEvent,
  useMemo,
  useContext,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Step,
  StepButton,
  Stepper,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import Add from '@material-ui/icons/Add';
import Check from '@material-ui/icons/Check';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useStyles } from '../../styles';
import '../../styles/addProjectModal.css';
import type {
  ProjectMappingType,
  ProjectFormData,
} from '../../interfaces/ui/addProjectModal';
import {
  isDuplicateProjectName,
  useFormValidation,
} from '../../hooks/useFormValidation';
import { FormField } from './FormField';
import { FormSelect } from './FormSelect';
import FormRadioGroup from './FormRadioGroup';
import { AcknowledgementSection } from './AcknowledgementSection';
import {
  ENVIRONMENT_OPTIONS,
  HELPER_MESSAGES,
  DEFAULT_FORM_DATA,
  FRONTEND_REPOSITORY_INFOTEXT,
  // PLAYWRIGHT_REPOSITORY_INFOTEXT,
  PROJECT_MAPPING_OPTIONS,
  REPOSITORY_CONFIG_OPTIONS,
  FRONTEND_REPOSITORY_BRANCH_INFOTEXT,
} from '../../constants';
import { useParams } from 'react-router-dom';
import { useMappedProjects } from '../../hooks/useMappedProjects';
import { useProjectTestPlans } from '../../hooks/useProjectTestPlans';
import { useProjectTestSuites } from '../../hooks/useProjectTestSuites';
import { ITPContext, TPContext } from '../../../../providers/TPProvider';
import TestPlanAndSuite from './TestPlanAndSuite';
import {
  getMappedProjectPlaceHolderText,
  getFlowPlaceHolderText,
  getResetDependentFormFields,
  getTestSuiteOptions,
} from './utils/helpers';
import { useGetServicesMapping } from '../../../../hooks/useGetServicesMapping';
import { getRepositoryOptions } from './utils/repositoryOptions';

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: ProjectFormData) => void;
  isSaving?: boolean;
  initialData?: ProjectFormData;
  initialErrors?: Record<string, string>;
  isEditing?: boolean;
  existingProjectNames?: string[];
}

export const AddProjectModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSaving,
  initialData,
  initialErrors = {},
  isEditing = false,
  existingProjectNames = [],
}: AddProjectModalProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const { projectId } = useParams();
  const [formData, setFormData] = useState<ProjectFormData>(
    initialData || DEFAULT_FORM_DATA,
  );
  const [errors, setErrors] = useState<Record<string, string>>(initialErrors);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>(
    {},
  );
  const { apmInfo }: ITPContext = useContext(TPContext);

  const {
    fetchMappedProjects,
    loadingMappedProjects,
    mappedProjects,
    clearMappedProjectsFetchError,
  } = useMappedProjects();

  const {
    fetchProjectTestPlans,
    loadingTestPlans,
    testPlans,
    clearTestPlansFetchError,
  } = useProjectTestPlans();

  const {
    fetchProjectTestSuites,
    loadingTestSuites,
    testSuites,
    clearTestSuitesFetchError,
  } = useProjectTestSuites();

  const {
    fetchProjectTestSuites: fetchExistingTestSuites,
    loadingTestSuites: loadingExistingTestSuites,
    testSuites: existingTestSuites,
    clearTestSuitesFetchError: clearExistingTestSuitesFetchError,
  } = useProjectTestSuites();

  const { validateField, validateForm, isFormValid } = useFormValidation();
  const validateProjectName = (value: string | boolean): string => {
    const error = validateField('projectName', value);
    if (error || isEditing || typeof value !== 'string') return error;

    return isDuplicateProjectName(value, existingProjectNames)
      ? 'This name is taken, please use another name'
      : '';
  };
  const [servicesMapping, loadingServicesMapping] = useGetServicesMapping(
    projectId!,
    isOpen,
  );

  const frontendRepositoryOptions = useMemo(
    () => getRepositoryOptions(servicesMapping, formData.repositoryConfig),
    [servicesMapping, formData.repositoryConfig],
  );

  const flowOptions = useMemo(() => {
    if (!apmInfo?.flows) return [];
    return apmInfo?.flows.map(flow => ({
      label: flow.Name,
      value: flow.RefId.toString(),
    }));
  }, [apmInfo?.flows]);

  const mappedProjectOptions = useMemo(() => {
    if (!formData.projectMappingType) return [];
    return mappedProjects.map(project => ({
      label: project.projectName,
      value: project.projectId,
    }));
  }, [mappedProjects]);

  const testPlanOptions = useMemo(() => {
    if (!formData.mappedProjectId) return [];
    return testPlans.map(testPlan => ({
      label: testPlan.name,
      value: testPlan.id.toString(),
    }));
  }, [testPlans, formData.mappedProjectId]);

  const testSuiteOptions = useMemo(() => {
    return getTestSuiteOptions(
      formData.projectMappingType,
      formData.testPlanId,
      testPlans,
      testSuites,
    );
  }, [testPlans, formData.testPlanId, testSuites, formData.projectMappingType]);

  const existingTestSuiteOptions = useMemo(() => {
    return getTestSuiteOptions(
      formData.projectMappingType,
      formData.existingTestPlanId,
      testPlans,
      existingTestSuites,
    );
  }, [
    testPlans,
    formData.existingTestPlanId,
    existingTestSuites,
    formData.projectMappingType,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setFormData({ ...DEFAULT_FORM_DATA, ...initialData });
    } else {
      // Opening create modal should always start from a fresh form state.
      resetForm();
    }

    setErrors(initialErrors);
    if (initialErrors && Object.keys(initialErrors).length > 0) {
      const touchedFields = Object.keys(initialErrors).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setTouched(prev => ({ ...prev, ...touchedFields }));
    }
  }, [isOpen, initialData, initialErrors]);

  const handleInputChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const fieldValue = type === 'checkbox' ? target.checked : value;

    if (name === 'repositoryConfig') {
      setFormData(prev => ({
        ...prev,
        repositoryConfig: value,
        frontendRepositoryName: '',
        frontendRepositoryUrl: '',
      }));
      setTouched(prev => ({ ...prev, frontendRepositoryName: false }));
      setErrors(prev => ({ ...prev, frontendRepositoryName: '' }));
      return;
    }

    if (name !== 'projectMappingType') {
      setFormData(prev => ({ ...prev, [name]: fieldValue }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    if (name === 'projectMappingType') {
      setFormData(prev => ({
        ...prev,
        projectMappingType: value as ProjectMappingType,
        ...getResetDependentFormFields(name),
      }));

      if (
        errors.projectMappingType ||
        errors.mappedProjectName ||
        errors.testPlanName ||
        errors.testSuiteName
      ) {
        setErrors(prev => ({
          ...prev,
          projectMappingType: '',
          mappedProjectName: '',
          testPlanName: '',
          testSuiteName: '',
        }));
        clearMappedProjectsFetchError();
        clearTestPlansFetchError();
        clearTestSuitesFetchError();
      }

      setTouched(prev => ({
        ...prev,
        mappedProjectName: false,
        testPlanName: false,
        testSuiteName: false,
      }));

      await fetchMappedProjects(projectId!, value as ProjectMappingType);
      return;
    }
  };

  const handleSelectChange = async (
    e: ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const { name, value } = e.target;
    if (!name) return;

    if (name === 'flowId') {
      const selectedFlow = flowOptions.find(flow => flow.value === value);
      setFormData(prev => ({
        ...prev,
        flowId: value as string,
        flowName: selectedFlow?.label || '',
      }));

      if (errors.flowId) {
        setErrors(prev => ({ ...prev, flowId: '' }));
      }

      return;
    }

    if (name === 'mappedProjectName') {
      const selectedProject = mappedProjects.find(
        project => project.projectId === value,
      );
      setFormData(prev => ({
        ...prev,
        mappedProjectId: value as string,
        mappedProjectName: selectedProject?.projectName || '',
        mappedProjectOrg: selectedProject?.projectOrg || '',
        ...getResetDependentFormFields(name),
      }));

      setErrors(prev => ({
        ...prev,
        mappedProjectName: '',
        testPlanName: '',
        testSuiteName: '',
      }));

      setTouched(prev => ({
        ...prev,
        testPlanName: false,
        testSuiteName: false,
      }));
      clearTestPlansFetchError();
      clearTestSuitesFetchError();
      clearExistingTestSuitesFetchError();

      if (selectedProject) {
        await fetchProjectTestPlans(
          projectId!,
          selectedProject,
          formData.projectMappingType,
        );
      }

      return;
    }

    if (name === 'frontendRepositoryName') {
      const selectedRepository = frontendRepositoryOptions.find(
        option => option.value === value,
      );
      setFormData(prev => ({
        ...prev,
        frontendRepositoryName: selectedRepository?.label || '',
        frontendRepositoryUrl: selectedRepository?.url || '',
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFieldBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement>,
  ) => {
    const { name } = e.target as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLButtonElement;

    setTouched(prev => ({ ...prev, [name]: true }));
    const value = formData[name as keyof ProjectFormData];
    const error =
      name === 'projectName'
        ? validateProjectName(value)
        : validateField(name, value);
    if (error) setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleTestPlanChange = async (value: string) => {
    const selectedPlan = testPlanOptions.find(o => o.value === value);

    setFormData((prev: ProjectFormData) => ({
      ...prev,
      testPlanId: value,
      testPlanName: selectedPlan?.label || '',
      ...getResetDependentFormFields('testPlanName'),
    }));

    setErrors(prev => ({
      ...prev,
      testPlanName: '',
      testSuiteName: '',
    }));

    setTouched(prev => ({
      ...prev,
      testSuiteName: false,
    }));
    clearTestSuitesFetchError();

    if (formData.projectMappingType === 'ADO') {
      const testPlan = testPlanOptions.find(tp => tp.value === value);
      if (testPlan) {
        await fetchProjectTestSuites(
          projectId!,
          formData.mappedProjectName,
          mappedProjects.find(
            project => project.projectId === formData.mappedProjectId,
          )?.projectOrg!,
          value,
          testPlan.label,
        );
      }
    }
  };

  const handleTestSuiteChange = (value: string) => {
    const selectedSuite = testSuiteOptions.find(o => o.value === value);

    setFormData((prev: ProjectFormData) => ({
      ...prev,
      testSuiteId: value,
      testSuiteName: selectedSuite?.label || '',
    }));

    setErrors(prev => ({ ...prev, testSuiteName: '' }));
  };

  const handleExistingTestPlanChange = async (value: string) => {
    const selectedPlan = testPlanOptions.find(o => o.value === value);
    setFormData(prev => ({
      ...prev,
      existingTestPlanId: value,
      existingTestPlanName: selectedPlan?.label || '',
      ...getResetDependentFormFields('existingTestPlan'),
    }));

    if (formData.projectMappingType === 'ADO' && selectedPlan) {
      await fetchExistingTestSuites(
        projectId!,
        formData.mappedProjectName,
        mappedProjects.find(p => p.projectId === formData.mappedProjectId)
          ?.projectOrg!,
        value,
        selectedPlan.label,
      );
    }
  };

  const handleExistingTestSuiteChange = (value: string) => {
    const selectedSuite = existingTestSuiteOptions.find(o => o.value === value);
    setFormData(prev => ({
      ...prev,
      existingTestSuiteId: value,
      existingTestSuiteName: selectedSuite?.label || '',
    }));
  };

  const handleAcknowledgementChange = (checked: boolean) => {
    setAcknowledgementChecked(checked);
    if (errors.acknowledgement)
      setErrors(prev => ({ ...prev, acknowledgement: '' }));
  };

  const handleNext = () => {
    const fieldsByStep = [
      ['projectName', 'appUrl', 'environment', 'flowId'],
      [
        'projectMappingType',
        'mappedProjectName',
        'testPlanName',
        'testSuiteName',
      ],
    ];
    const fields = fieldsByStep[activeStep];
    if (!fields) return;

    const validationErrors = validateForm(formData, true);
    const projectNameError = validateProjectName(formData.projectName);
    if (projectNameError) validationErrors.projectName = projectNameError;
    const stepErrors = fields.reduce((errorsForStep, field) => {
      if (validationErrors[field]) {
        errorsForStep[field] = validationErrors[field];
      }
      return errorsForStep;
    }, {} as Record<string, string>);

    setErrors(prev => ({
      ...prev,
      ...fields.reduce(
        (updatedErrors, field) => ({
          ...updatedErrors,
          [field]: stepErrors[field] || '',
        }),
        {},
      ),
    }));
    setTouched(prev => ({
      ...prev,
      ...fields.reduce(
        (updatedTouched, field) => ({ ...updatedTouched, [field]: true }),
        {},
      ),
    }));

    if (Object.keys(stepErrors).length > 0) return;

    setCompletedSteps(prev => ({ ...prev, [activeStep]: true }));
    setActiveStep(prev => prev + 1);
  };

  const handleStepClick = (step: number) => {
    if (completedSteps[step]) setActiveStep(step);
  };

  const handleSubmit = () => {
    if (activeStep !== 2 || isSaving) return;

    const newErrors = validateForm(formData, acknowledgementChecked);
    const projectNameError = validateProjectName(formData.projectName);
    if (projectNameError) newErrors.projectName = projectNameError;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        projectName: true,
        appUrl: true,
        environment: true,
        projectMappingType: true,
        mappedProjectName: true,
        testPlanName: true,
        testSuiteName: true,
        flowId: true,
        acknowledgement: true,
      });
      return;
    }
    onSubmit?.(formData);
  };

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setAcknowledgementChecked(false);
    setActiveStep(0);
    setCompletedSteps({});
    setErrors({});
    setTouched({});
    clearMappedProjectsFetchError();
    clearTestPlansFetchError();
    clearTestSuitesFetchError();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const mode = isEditing ? 'edit' : 'create';
  const isValid = isFormValid(formData, acknowledgementChecked);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth={true}
      aria-labelledby="add-project-modal-title"
      PaperProps={{
        className: `apm-paper ${classes.addProjectModalPaper}`,
        style: { position: 'relative' },
      }}
    >
      <Backdrop
        open={!!isSaving}
        style={{
          position: 'absolute',
          zIndex: 9999,
          borderRadius: 'inherit',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box className="apm-saving-state">
          <CircularProgress color="inherit" />
          <Typography className="apm-saving-text">Submitting...</Typography>
        </Box>
      </Backdrop>
      <DialogTitle disableTypography className="apm-header">
        <Typography
          variant="h6"
          id="add-project-modal-title"
          className="apm-title"
        >
          {mode === 'create' ? 'Create Project' : 'Edit Project'}
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          className="apm-close-btn"
          aria-label="Close modal"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="apm-content">
        <Stepper activeStep={activeStep} nonLinear>
          {['Basic Info', 'Target Test Plan', 'Repo Context'].map(
            (label, index) => (
              <Step key={label} completed={completedSteps[index]}>
                <StepButton
                  onClick={() => handleStepClick(index)}
                  disabled={index === activeStep || !completedSteps[index]}
                >
                  {!isMobile && label}
                </StepButton>
              </Step>
            ),
          )}
        </Stepper>
        <form
          id="add-project-form"
          onSubmit={event => event.preventDefault()}
          noValidate
        >
          {activeStep === 0 && (
            <>
              <FormField
                id="projectName"
                label="Project Name"
                type="text"
                placeholder="Enter project name"
                value={formData.projectName}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={touched.projectName ? errors.projectName : undefined}
                required
              />
              <FormField
                id="appUrl"
                label="Non-Prod App URL"
                type="text"
                placeholder="https://www.example.com"
                value={formData.appUrl}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={touched.appUrl ? errors.appUrl : undefined}
                required
              />
              <FormControlLabel
                className="apm-login-checkbox-label"
                control={
                  <Checkbox
                    checked={formData.loginNotRequired}
                    onChange={handleInputChange}
                    id="loginNotRequired"
                    name="loginNotRequired"
                    color="primary"
                    size="small"
                  />
                }
                label="Login not required for this application?"
              />
              <FormSelect
                id="environment"
                label="Environment"
                value={formData.environment}
                onChange={handleSelectChange}
                onBlur={handleFieldBlur}
                options={ENVIRONMENT_OPTIONS}
                error={touched.environment ? errors.environment : undefined}
                required
              />
              <FormSelect
                id="flowId"
                label="Flow Id"
                value={isEditing ? formData.flowName : formData.flowId}
                onChange={handleSelectChange}
                onBlur={handleFieldBlur}
                options={flowOptions}
                error={touched.flowId ? errors.flowId : undefined}
                placeholder={getFlowPlaceHolderText(flowOptions.length)}
                disabled={isEditing}
                required
                hasNoData={flowOptions.length === 0}
              />
            </>
          )}

          {activeStep === 1 && (
            <section className="apm-form-section-group">
              <FormRadioGroup
                id="projectMappingType"
                label="Target Test Plan"
                value={formData.projectMappingType}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                options={PROJECT_MAPPING_OPTIONS}
                error={
                  touched.projectMappingType
                    ? errors.projectMappingType
                    : undefined
                }
                disabled={isEditing}
                required
              />
              <FormSelect
                id="mappedProjectName"
                label="Mapped Project"
                value={formData.mappedProjectId}
                onChange={handleSelectChange}
                onBlur={handleFieldBlur}
                options={mappedProjectOptions}
                error={
                  touched.mappedProjectName && mappedProjectOptions.length > 0
                    ? errors.mappedProjectName
                    : undefined
                }
                placeholder={getMappedProjectPlaceHolderText(
                  formData.projectMappingType,
                  loadingMappedProjects,
                  mappedProjectOptions.length,
                )}
                disabled={
                  isEditing ||
                  !formData.projectMappingType ||
                  loadingMappedProjects
                }
                required
                loading={loadingMappedProjects}
                hasNoData={
                  !isEditing &&
                  !!formData.projectMappingType &&
                  !loadingMappedProjects &&
                  mappedProjectOptions.length === 0
                }
              />
              <TestPlanAndSuite
                testPlanId={formData.testPlanId}
                testSuiteId={formData.testSuiteId}
                projectMappingType={formData.projectMappingType}
                mappedProjectId={formData.mappedProjectId}
                testPlanOptions={testPlanOptions}
                testSuiteOptions={testSuiteOptions}
                touched={{
                  testPlanName: !!touched.testPlanName,
                  testSuiteName: !!touched.testSuiteName,
                }}
                errors={{
                  testPlanName: errors.testPlanName,
                  testSuiteName: errors.testSuiteName,
                }}
                isEditing={isEditing}
                loadingTestPlans={loadingTestPlans}
                loadingTestSuites={loadingTestSuites}
                required
                onTestPlanChange={handleTestPlanChange}
                onTestSuiteChange={handleTestSuiteChange}
                onFieldBlur={handleFieldBlur}
              />
            </section>
          )}

          {activeStep === 2 && (
            <>
              <Typography>
                Already have a test plan? Select it below to execute alongside
                AI-generated tests (optional)
              </Typography>
              <section className="apm-form-section-group">
                <TestPlanAndSuite
                  testPlanId={formData.existingTestPlanId}
                  testSuiteId={formData.existingTestSuiteId}
                  projectMappingType={formData.projectMappingType}
                  mappedProjectId={formData.mappedProjectId}
                  testPlanOptions={testPlanOptions}
                  testSuiteOptions={existingTestSuiteOptions}
                  touched={{}}
                  errors={{}}
                  isEditing={isEditing}
                  loadingTestPlans={loadingTestPlans}
                  loadingTestSuites={loadingExistingTestSuites}
                  required={false}
                  onTestPlanChange={handleExistingTestPlanChange}
                  onTestSuiteChange={handleExistingTestSuiteChange}
                  onFieldBlur={handleFieldBlur}
                />
              </section>
              <section className="apm-form-section-group">
                <FormRadioGroup
                  id="repositoryConfig"
                  label="Repository config"
                  value={formData.repositoryConfig}
                  onChange={handleInputChange}
                  options={REPOSITORY_CONFIG_OPTIONS}
                />
                <FormSelect
                  id="frontendRepositoryName"
                  label="Frontend Repository"
                  value={formData.frontendRepositoryName}
                  onChange={handleSelectChange}
                  onBlur={handleFieldBlur}
                  options={frontendRepositoryOptions}
                  placeholder={
                    !formData.repositoryConfig
                      ? 'Select repository config first'
                      : loadingServicesMapping
                      ? 'Loading repositories...'
                      : frontendRepositoryOptions.length === 0
                      ? 'No repositories found'
                      : 'Select a frontend repository'
                  }
                  disabled={
                    !formData.repositoryConfig || loadingServicesMapping
                  }
                  loading={loadingServicesMapping}
                  hasNoData={
                    Boolean(formData.repositoryConfig) &&
                    !loadingServicesMapping &&
                    frontendRepositoryOptions.length === 0
                  }
                  infoText={FRONTEND_REPOSITORY_INFOTEXT}
                />
                <FormField
                  id="frontendRepositoryBranch"
                  label="Frontend Repository Branch"
                  type="text"
                  placeholder="Enter branch name, e.g., main, develop."
                  value={formData.frontendRepositoryBranch}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  infoText={FRONTEND_REPOSITORY_BRANCH_INFOTEXT}
                />

                {/* <FormField
                  id="playwrightTestRepositoryUrl"
                  label="Playwright Test Repository URL"
                  type="url"
                  placeholder="Enter Test automation Repo url"
                  value={formData.playwrightTestRepositoryUrl}
                  onChange={handleInputChange}
                  onBlur={handleFieldBlur}
                  error={
                    touched.playwrightTestRepositoryUrl
                      ? errors.playwrightTestRepositoryUrl
                      : undefined
                  }
                  infoText={PLAYWRIGHT_REPOSITORY_INFOTEXT}
                /> */}
              </section>

              {!formData.loginNotRequired && (
                <AcknowledgementSection
                  checked={acknowledgementChecked}
                  onChange={handleAcknowledgementChange}
                  error={
                    touched.acknowledgement ? errors.acknowledgement : undefined
                  }
                />
              )}
            </>
          )}
        </form>
      </DialogContent>

      <DialogActions className="apm-actions">
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          className="apm-cancel-btn"
          aria-label="Cancel"
        >
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setActiveStep(prev => prev - 1)}
            className="apm-cancel-btn"
            aria-label="Back"
          >
            Back
          </Button>
        )}
        <Box className="apm-submit-group">
          {activeStep < 2 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              className="apm-submit-btn"
              aria-label="Next"
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={!isValid}
                className="apm-submit-btn"
                aria-label={
                  mode === 'create' ? 'Add New Project' : 'Update Project'
                }
                startIcon={mode === 'create' ? <Add /> : <Check />}
              >
                {mode === 'create' ? 'Add New Project' : 'Update Project'}
              </Button>
              {!isValid && (
                <Typography className="apm-helper-text">
                  {HELPER_MESSAGES[mode]}
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};
